/**
 * AdminDonationPagesPage — list + create + edit standalone donation pages.
 * Each page lives at /give/{slug} and has its own Stripe-backed give widget.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Plus, Save, Trash2, ExternalLink, HandHeart, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { MediaUpload } from '@/components/MediaUpload';

const empty = () => ({
  slug: '',
  title: '',
  subtitle: '',
  hero_image_url: '',
  story_markdown: '',
  goal_amount: 1000,
  currency: 'USD',
  suggested_amounts: [25, 50, 100, 250],
  bank_details: '',
  whatsapp_confirm: '',
  organizer_name: '',
  organizer_email: '',
  theme_primary: '#0f766e',
  theme_accent: '#f97316',
  is_published: true,
});

const AdminDonationPagesPage = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null = list view; object = edit/create form

  const { data: pages = [], isLoading } = useQuery({
    queryKey: ['donation-pages-admin'],
    queryFn: () => api.get('/donation-pages/admin').then((r) => r.data),
  });

  const create = useMutation({
    mutationFn: (payload) => api.post('/donation-pages/admin', payload),
    onSuccess: (r) => {
      toast.success('Donation page created');
      qc.invalidateQueries({ queryKey: ['donation-pages-admin'] });
      setEditing(r.data);
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Create failed'),
  });

  const save = useMutation({
    mutationFn: ({ slug, ...payload }) => api.put(`/donation-pages/admin/${slug}`, payload),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['donation-pages-admin'] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: (slug) => api.delete(`/donation-pages/admin/${slug}`),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['donation-pages-admin'] });
      setEditing(null);
    },
  });

  // ----- list view -----
  if (!editing) {
    return (
      <div className="p-6 max-w-6xl mx-auto" data-testid="donation-pages-admin">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <HandHeart className="h-5 w-5 text-slate-500" />
            Donation Pages
          </h1>
          <Button onClick={() => setEditing(empty())} data-testid="donation-pages-new">
            <Plus className="h-4 w-4 mr-1" /> New Donation Page
          </Button>
        </div>
        {isLoading ? (
          <div className="py-20 text-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…
          </div>
        ) : pages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <HandHeart className="h-10 w-10 mx-auto opacity-30 mb-2" />
              <p className="text-sm mb-3">No donation pages yet.</p>
              <Button onClick={() => setEditing(empty())} data-testid="donation-pages-new-empty">
                <Plus className="h-4 w-4 mr-1" /> Create your first
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pages.map((p) => (
              <Card key={p.id} className="hover:shadow-md transition" data-testid={`donation-page-card-${p.slug}`}>
                <CardContent className="p-0">
                  <div
                    className="h-32 bg-cover bg-center"
                    style={{ backgroundImage: p.hero_image_url ? `url(${p.hero_image_url})` : `linear-gradient(135deg, ${p.theme_primary || '#0f766e'}, ${p.theme_accent || '#f97316'})` }}
                  />
                  <div className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate flex-1">{p.title}</h3>
                      <Badge variant={p.is_published ? 'default' : 'secondary'}>
                        {p.is_published ? 'Live' : 'Draft'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500">/give/{p.slug}</p>
                    {p.subtitle && <p className="text-sm text-slate-600 line-clamp-2">{p.subtitle}</p>}
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => setEditing(p)} data-testid={`donation-page-edit-${p.slug}`}>
                        Edit
                      </Button>
                      <Link to={`/give/${p.slug}`} target="_blank">
                        <Button size="sm" variant="ghost" data-testid={`donation-page-view-${p.slug}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ----- edit/create form -----
  const draft = editing;
  const isNew = !draft.id;
  const set = (patch) => setEditing({ ...draft, ...patch });

  const submit = () => {
    if (!draft.title || !draft.slug) {
      toast.error('Slug and title are required');
      return;
    }
    const payload = { ...draft };
    if (isNew) {
      create.mutate(payload);
    } else {
      const { id, slug, created_at, updated_at, ...rest } = payload;
      save.mutate({ slug, ...rest });
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="donation-page-editor">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>← Back to pages</Button>
          <h1 className="text-2xl font-semibold mt-1">
            {isNew ? 'New Donation Page' : `Editing: ${draft.title}`}
          </h1>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Link to={`/give/${draft.slug}`} target="_blank">
              <Button size="sm" variant="outline" data-testid="editor-view-public">
                <ExternalLink className="h-4 w-4 mr-1" /> View
              </Button>
            </Link>
          )}
          <Button onClick={submit} disabled={create.isPending || save.isPending} data-testid="editor-save">
            {(create.isPending || save.isPending) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            {isNew ? 'Create' : 'Save'}
          </Button>
          {!isNew && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600"
              onClick={() => window.confirm('Delete this donation page permanently?') && remove.mutate(draft.slug)}
              data-testid="editor-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="giving">Giving</TabsTrigger>
          <TabsTrigger value="theme">Theme</TabsTrigger>
          <TabsTrigger value="gifts" disabled={isNew}>Gifts</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-4 pt-4">
          <Card><CardContent className="space-y-3 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Slug (URL: /give/...)</Label>
                <Input
                  value={draft.slug}
                  disabled={!isNew}
                  onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="abmhc-2026"
                  data-testid="editor-slug"
                />
              </div>
              <div>
                <Label>Organizer name</Label>
                <Input value={draft.organizer_name || ''} onChange={(e) => set({ organizer_name: e.target.value })} data-testid="editor-organizer" />
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={draft.title} onChange={(e) => set({ title: e.target.value })} data-testid="editor-title" />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={draft.subtitle || ''} onChange={(e) => set({ subtitle: e.target.value })} data-testid="editor-subtitle" />
            </div>
            <div>
              <MediaUpload value={draft.hero_image_url || ''} onChange={(url) => set({ hero_image_url: url })} purpose="hero" label="Hero image" testId="editor-hero" />
            </div>
            <div>
              <Label>Story (supports line breaks)</Label>
              <Textarea
                rows={10}
                value={draft.story_markdown || ''}
                onChange={(e) => set({ story_markdown: e.target.value })}
                data-testid="editor-story"
              />
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <div>
                <Label>Published</Label>
                <p className="text-xs text-slate-500">When off, /give/{draft.slug} returns 404.</p>
              </div>
              <Switch
                checked={!!draft.is_published}
                onCheckedChange={(v) => set({ is_published: v })}
                data-testid="editor-publish"
              />
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="giving" className="space-y-4 pt-4">
          <Card><CardContent className="space-y-3 pt-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label>Currency</Label>
                <Input value={draft.currency} onChange={(e) => set({ currency: e.target.value.toUpperCase().slice(0, 3) })} data-testid="editor-currency" />
              </div>
              <div className="col-span-2">
                <Label>Goal amount</Label>
                <Input type="number" value={draft.goal_amount} onChange={(e) => set({ goal_amount: Number(e.target.value) })} data-testid="editor-goal" />
              </div>
            </div>
            <div>
              <Label>Suggested amounts (comma separated)</Label>
              <Input
                value={(draft.suggested_amounts || []).join(', ')}
                onChange={(e) => set({ suggested_amounts: e.target.value.split(',').map((s) => Number(s.trim())).filter((n) => n > 0) })}
                data-testid="editor-amounts"
              />
            </div>
            <div>
              <Label>Bank transfer details (shown as a collapsible block)</Label>
              <Textarea rows={4} value={draft.bank_details || ''} onChange={(e) => set({ bank_details: e.target.value })} data-testid="editor-bank" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>WhatsApp confirm number</Label>
                <Input value={draft.whatsapp_confirm || ''} onChange={(e) => set({ whatsapp_confirm: e.target.value })} placeholder="+263771234567" />
              </div>
              <div>
                <Label>Organizer email</Label>
                <Input value={draft.organizer_email || ''} onChange={(e) => set({ organizer_email: e.target.value })} />
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="theme" className="space-y-4 pt-4">
          <Card><CardContent className="space-y-3 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary color</Label>
                <Input type="color" className="h-10" value={draft.theme_primary || '#0f766e'} onChange={(e) => set({ theme_primary: e.target.value })} />
              </div>
              <div>
                <Label>Accent color (buttons)</Label>
                <Input type="color" className="h-10" value={draft.theme_accent || '#f97316'} onChange={(e) => set({ theme_accent: e.target.value })} />
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="gifts" className="space-y-4 pt-4">
          {!isNew && <GiftsList slug={draft.slug} />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const GiftsList = ({ slug }) => {
  const { data: gifts = [], isLoading } = useQuery({
    queryKey: ['donation-page-gifts', slug],
    queryFn: () => api.get(`/donation-pages/admin/${slug}/gifts`).then((r) => r.data),
  });
  if (isLoading) {
    return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }
  if (!gifts.length) {
    return <Card><CardContent className="py-12 text-center text-sm text-slate-500">No gifts received yet.</CardContent></Card>;
  }
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">{gifts.length} gifts</CardTitle></CardHeader>
      <CardContent>
        <ul className="divide-y">
          {gifts.map((g) => (
            <li key={g.id} className="py-2 flex items-center justify-between text-sm">
              <div>
                <p className="font-medium">{g.is_anonymous ? 'Anonymous' : (g.donor_name || 'Friend')}</p>
                {g.donor_email && !g.is_anonymous && <p className="text-xs text-slate-500">{g.donor_email}</p>}
                {g.message && <p className="text-xs italic text-slate-600 mt-0.5">&ldquo;{g.message}&rdquo;</p>}
                <p className="text-[11px] text-slate-400">{(g.created_at || '').slice(0, 16).replace('T', ' ')}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold">{(g.currency || 'usd').toUpperCase()} {g.amount}</p>
                <Badge variant={g.status === 'paid' || g.status === 'complete' ? 'default' : 'secondary'} className="text-xs">{g.status}</Badge>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default AdminDonationPagesPage;
